import { fetchFreeLimit } from "@common-reducers/free-limit-slice";
import { hydrateSubscriptionsThunk } from "@common-reducers/subscription-hydration";
import { setUserSegment } from "@common-services/analytics-config";
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";
import { globalHistory } from "@common-services/global-history";

import { createAsyncThunk } from "@reduxjs/toolkit";

import { Subscription } from "../../bt-common/schemas/bt/subscription";
import { DBSessionThunks, SessionReducerActions } from "@common-reducers/DBServiceThunks";
import { BLUETICKS_TOKEN_KEY } from "@common/reusable/common";
import { isWithinDebounce } from "@common/noifier-module/reconnect-policy";
import { readReferralCode } from "@common/utils/referral-code";
import { MessageServiceActions } from "@common/services/messageServiceActions";
import { notifyBackgroundOfLogout } from "@common/bt-auth/logout-bridge";
import { isWireError } from "@common/services/post-message-to-whatsapp-functions";
import { getPendingUserMessages } from "@common/thunks/UserMessagesThunks";
import { CommonRootState } from "@common/types/common-root-state-type";
import { accessFormPopupActions } from "./AccessFormPopupReducer";
import { toggleSocketModal } from "./MainReducer";

import { backendApiEndpoints, sendEmailFromClient } from "./backend-api/backend-api";
import { selectCurrentSessionId, selectUserId } from "./UserSelectors";
import { flowPath } from "@common/utils/clear-flow";

//import { googleLogout } from '@react-oauth/google';
import {
  authenticate,
  backendApi,
  cancelSubscription,
  changePlan,
  updateChargeDate
} from "@common-reducers/backend-api/backend-api";


import { BackgorundMessageServiceActions } from "@/backgroundMessageServiceActions";
import {
  boardsReducerActions, DBBoardsThunks,
  DBSubscriptionThunks,
  DBUserThunks,
  SubscriptionReducerActions, userSelectByQuery
} from "@common-reducers/DBServiceThunks";
import { activeSubscriptionSelector } from "@common-reducers/UserSelectors";
import { isExtensionContext } from "@common-services/detect-context";
import { User } from "@common/models/user";
import { setDarkModeClasses } from "@common-services/utils";
import { ContentMessageServiceActions } from "@extension/context/content/services/contentMessageServiceActions";
import { disconnectNotifier } from "../noifier-module/NotifierThunks";
import { DBReducerMap } from "./DBServiceThunks";
import { mainSliceActions } from "./MainReducer";
import { modalActions } from "./ModalReducer";
import { taskActions } from "./TasksReducer";
import { logout, setUserDirection, setAuthenticationStatus, setAuthError, setUser } from "./UserReducer";
import { selectCurrentWorkspace } from "./UserSelectors";
import { getPendingAndRuningCampaigns } from "./CampaignThunks";
import { labelActions } from "./labels-reducer";
import { getWorkspaceDataThunk } from "./WorkspaceThunks";
import i18n from "i18next";
import { applyDocumentDirection, getDirectionForLanguage } from "@common-services/rtl";


export const logoutWrapperThunk = createAsyncThunk<void, { accessToken: string | null }, { state: CommonRootState }>(
  "logoutThunk",
  ({ accessToken }, thunkApi) => {
    const { dispatch } = thunkApi;
    console.log('[login-flow] logoutWrapperThunk: entry (the UI Log Out path)', {
      hasAccessToken: !!accessToken,
      ctx: (typeof chrome !== 'undefined' && chrome.runtime?.id) ? 'extension/content' : 'web-page-world',
    });
    dispatch(logoutThunk());


    if (accessToken) {
      window.localStorage.removeItem(BLUETICKS_TOKEN_KEY);
      const url = new URL(window.location.href);
      url.searchParams.delete("accessToken");
      window?.history?.replaceState(null, "", url);
    }
    //never navigate to /login in whatsApp!
    if (!isExtensionContextOrWhatsappView()) {
      // Read the flow BEFORE navigating: logging out of a /clear-* page must land
      // on /clear-login, not drop the user into the full product's login.
      globalHistory?.push(flowPath("/login"));
    }

    // Bridge the logout to the background chrome.storage hub so the popup (and any
    // other surface live-syncing the hub) logs out too. This is the logout
    // counterpart to persistToken's login bridge. clearStoredToken bridges the same
    // way, but the UI Log Out path goes through THIS thunk and never calls
    // clearStoredToken — so without bridging here the hub stays populated and the
    // popup stays logged in. logoutThunk (dispatched above) already messages the
    // background "logout" directly, which covers the extension/content context; that
    // call is a no-op in the web page world (no chrome.runtime.id). So bridge ONLY the
    // page world here (skipDirectBackgroundMessage) to avoid a redundant second
    // "logout" to the background in the extension.
    console.log("[login-flow] logoutWrapperThunk: bridging logout to background hub (page-world only)");
    notifyBackgroundOfLogout({ skipDirectBackgroundMessage: true });
  }
);

export const openSocketModalThunk = createAsyncThunk<void, void, { state: CommonRootState }>(
  "openSocketModalThunk",
  async (_, thunkApi) => {
    const { dispatch } = thunkApi;
    const state = thunkApi.getState();

    const userId = selectUserId(state);
    if (!userId) {
      dispatch(accessFormPopupActions.setStatus("Signup"));
    } else {
      const sessionId = selectCurrentSessionId(state);
      if (sessionId) dispatch(SessionReducerActions.removeOne(sessionId));
      dispatch(toggleSocketModal(true));
    }
  }
);

export const createNewSessionThunk = createAsyncThunk<void, void, { state: CommonRootState }>(
  "createNewSessionThunk",
  async (_, thunkApi) => {
    const { dispatch } = thunkApi;
    await dispatch(SessionReducerActions.clearState());
    await dispatch(backendApiEndpoints.createUserSession.initiate());
    await dispatch(DBSessionThunks.find({}));
  }
);

// Validate that the user does not have redundant subscriptions
export const validateSubscriptionsThunk = createAsyncThunk<void, Subscription[], { state: CommonRootState }>(
  "validateSubscriptionsThunk",
  async (subscriptionList: Subscription[], thunkApi) => {
    const { dispatch } = thunkApi;


    const hasActiveChargebee = subscriptionList.some(sub =>
      sub.type === 'chargebee' && sub.status.toLowerCase() === 'active'
    );

    // Get all active Bluesnap subscriptions
    const activeBluesnapSubs = subscriptionList.filter(sub =>
      sub.type === 'bluesnap' && sub.origStatus?.toUpperCase() === 'ACTIVE'
    );

    // Check if there's at least one active Bluesnap subscription
    const hasActiveBluesnap = activeBluesnapSubs.length > 0;

    // If both conditions are met, return the active Bluesnap subscriptions
    if (hasActiveChargebee && hasActiveBluesnap) {
      for (const sub of activeBluesnapSubs) {
        if (sub._id === undefined) continue;
        await dispatch(backendApiEndpoints.cancelSubscription.initiate({
          subscriptionID: sub._id,
          reason: 'Redundant subscription'
        }));
      }
    }
  }
);


// Module-level timestamp of the last live-data load, used to debounce best-effort "catch-up" callers
// (the MQTT gap-resync, the post-auth re-load) so two refetches landing close together — e.g. a
// reconnect-resync coinciding with a storage-sync re-auth — collapse into one. Every call updates it;
// only callers that pass skipIfLoadedWithinMs opt into skipping. Reset on logout/account-switch so a
// fresh login always loads (see clearStateThunk).
let lastLiveDataLoadAt = 0;
export const resetLiveDataLoadDebounce = () => { lastLiveDataLoadAt = 0; };

// create loadUserMessagesAndCampaignsThunk
export const loadUserMessagesAndCampaignsThunk = createAsyncThunk<void, { skipIfLoadedWithinMs?: number } | void, { state: CommonRootState }>(
  "loadUserMessagesAndCampaignsThunk",
  async (arg, thunkApi) => {
    const { dispatch } = thunkApi;
    const skipMs = arg && typeof arg === 'object' ? arg.skipIfLoadedWithinMs : undefined;
    const now = Date.now();
    if (skipMs && isWithinDebounce(lastLiveDataLoadAt, now, skipMs)) {
      // A load happened moments ago (post-auth / component / prior resync) — data is already fresh.
      return;
    }
    lastLiveDataLoadAt = now;
    dispatch(getPendingAndRuningCampaigns())
    dispatch(getPendingUserMessages())
    // #131 T2 — pull the free-limit policy ONCE, here at boot, so it is already settled by
    // the time the composer opens. Fetching it lazily on composer-open raced the first
    // render and flashed a control paywall at treatment-arm users. See free-limit-slice.ts.
    dispatch(fetchFreeLimit())
  }
);


export const switchWorkspace = createAsyncThunk<
  { success: boolean },
  { workspaceId: string },
  { state: CommonRootState }
>("switchWorkspace", async ({ workspaceId }, thunkApi) => {
  const { dispatch, getState } = thunkApi;
  const state: CommonRootState = getState();
  const userId = state.UserReducer.userId;
  const currentWorkspace = selectCurrentWorkspace(state);

  if (currentWorkspace?._id === workspaceId) {
    return { success: false };
  }

  dispatch(modalActions.cleanComponentToRender());

  // Update user's currentWorkspace directly in Redux to avoid triggering authenticateOnQueryStarted
  // which would clear all state
  const { UserReducerActions } = await import(/* webpackMode: "eager" */ "@common-reducers/DBServiceThunks");
  const { setUser } = await import(/* webpackMode: "eager" */ "@common-reducers/UserReducer");
  
  // Update in DBUserReducer
  dispatch(UserReducerActions.updateOne({
    id: userId!,
    changes: { currentWorkspace: workspaceId }
  }));

  // Also update in UserReducer.user so selectors pick up the change immediately
  const currentUser = state.UserReducer.user;
  if (currentUser) {
    dispatch(setUser({
      ...currentUser,
      currentWorkspace: workspaceId as any
    }));
  }

  // Also dispatch patch to backend but don't await it - let it happen asynchronously
  // The backend will update the database but won't affect the frontend state
  dispatch(
    DBUserThunks.patch({
      entity: {
        _id: userId,
        currentWorkspace: workspaceId,
      },
    })
  );

  // Clear old boards and load all new workspace data atomically
  const { boardsReducerActions } = await import(/* webpackMode: "eager" */ "@common-reducers/DBServiceThunks");
  dispatch(boardsReducerActions.clearState());
  
  await dispatch(getWorkspaceDataThunk({ workspaceId }));
  // FE#167 — route the entitlement fetch through the hydration tracker so a failure is
  // recorded rather than silently leaving itemsLoaded false forever (which the gates
  // cannot distinguish from "still loading"). Resolves even on exhaustion: a failed
  // entitlement fetch must not abort login — the gates hold and the dialog explains.
  await dispatch(hydrateSubscriptionsThunk() as any);
  // Load boards that belong to the new workspace
  await dispatch(DBBoardsThunks.find({ 
    workspace: workspaceId, 
    deleted: false 
  }));

  return { success: true };
});

export const cancelSubscriptionThunk = createAsyncThunk<
  void,
  { subscriptionID: string; reason: string },
  { state: CommonRootState }
>("cancelSubscriptionThunk", async ({ subscriptionID, reason }, thunkApi) => {
  const { dispatch } = thunkApi;

  const res = await dispatch(
    cancelSubscription.initiate({
      subscriptionID,
      reason,
    })
  );

  if ("data" in res) {
    dispatch(refreshUser());
  }
});

export const updateChargeDateThunk = createAsyncThunk<
  void,
  { subscriptionId: string; nextChargeDate: string },
  { state: CommonRootState }
>("updateChargeDate", async ({ subscriptionId, nextChargeDate }, thunkApi) => {
  const { dispatch } = thunkApi;

  await dispatch(
    updateChargeDate.initiate({
      subscriptionId,
      nextChargeDate,
    })
  );


});

export const toggleDarkMode = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("toggleDarkMode", (_, thunkApi) => {
  const state = thunkApi.getState();
  const { dispatch } = thunkApi;
  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: state.UserReducer.userId,
  });
  const isDarkMode = !user?.isDarkMode;

  console.log(`Dark mode: ${isDarkMode}`);

  dispatch(
    DBUserThunks.patch({
      entity: {
        _id: user._id,
        isDarkMode,
      },
    })
  );

  // Keep in-memory user in sync so selectors update immediately
  if (state?.UserReducer?.user) {
    dispatch(setUser({ ...(state.UserReducer.user as any), isDarkMode }));
  }

  // Update dark mode classes for immediate effect - uses shared function for consistency
  setDarkModeClasses(isDarkMode);
});

/**
 * Persist the keep-WhatsApp-awake preference and tell the background service
 * worker about it. The user document is the source of truth; the postMessage
 * lets the extension's wake loop honor the choice immediately (the bridges in
 * attribution-bridge.ts / contentMessageService.ts forward it to background).
 */
export const setKeepWhatsappAwake = createAsyncThunk<
  void,
  boolean,
  { state: CommonRootState }
>("setKeepWhatsappAwake", (enabled, thunkApi) => {
  const state = thunkApi.getState();
  const { dispatch } = thunkApi;
  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: state.UserReducer.userId,
  });
  if (!user) return;

  dispatch(
    DBUserThunks.patch({
      entity: {
        _id: user._id,
        keepWhatsappTabAwake: enabled,
      },
    })
  );

  // Keep in-memory user in sync so selectors update immediately.
  if (state?.UserReducer?.user) {
    dispatch(setUser({ ...(state.UserReducer.user as any), keepWhatsappTabAwake: enabled }));
  }

  // Notify the extension background (via the content-script bridge) so the
  // wake loop stops/starts switching tabs without waiting for a reload.
  try {
    window.postMessage({ type: 'SET_KEEP_AWAKE', enabled }, '*');
  } catch { /* no window (SSR/worker) — ignore */ }
});

/**
 * Persist the btgpt "Files auto-recognition" permission. The user document is
 * the source of truth (same pattern as setKeepWhatsappAwake): the extension's
 * linked-AI-chat handler reads it from its synced user doc, so a toggle in the
 * btgpt web UI takes effect on the WhatsApp tab without any reload.
 */
export const setBtgptFilesAutoRecognition = createAsyncThunk<
  void,
  boolean,
  { state: CommonRootState }
>("setBtgptFilesAutoRecognition", (enabled, thunkApi) => {
  const state = thunkApi.getState();
  const { dispatch } = thunkApi;
  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: state.UserReducer.userId,
  });
  if (!user) return;

  dispatch(
    DBUserThunks.patch({
      entity: {
        _id: user._id,
        btgptFilesAutoRecognition: enabled,
      },
    })
  );

  // Keep in-memory user in sync so selectors update immediately.
  if (state?.UserReducer?.user) {
    dispatch(setUser({ ...(state.UserReducer.user as any), btgptFilesAutoRecognition: enabled }));
  }
});

export const toggleDomDirection = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("toggleDomDirection", (_, thunkApi) => {
  const state = thunkApi.getState();
  const { dispatch } = thunkApi;
  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: state.UserReducer.userId,
  });
  const language = i18n.language || user?.preferredLanguage || "en";
  const direction = getDirectionForLanguage(language);
  applyDocumentDirection(language);

  dispatch(setUserDirection(direction));

  dispatch(
    DBUserThunks.patch({
      entity: {
        ...user,
        direction,
      },
    })
  );
});

export const changeDefaultChatCountryCode = createAsyncThunk<
  void,
  { defaultChatCountryCode: string },
  { state: CommonRootState }
>(
  "changeDefaultChatCountryCode",
  async ({ defaultChatCountryCode }, thunkApi) => {
    const state = thunkApi.getState();
    const { dispatch } = thunkApi;

    const [user] = userSelectByQuery(state.DBUserReducer, {
      _id: state.UserReducer.userId,
    });

    if (user.defaultChatCountryCode !== defaultChatCountryCode) {

      dispatch(DBUserThunks.patch({
        entity: {
          _id: state.UserReducer.userId,
          defaultChatCountryCode,
        },
      })
      );
    }
  }
);

export const changePaymentPlan = createAsyncThunk<
  { changeSuccessful: boolean },
  { productId: string },
  { state: CommonRootState }
>("changePaymentPlan", async ({ productId }, thunkApi) => {
  const { dispatch } = thunkApi;
  let res: { changeSuccessful: boolean } = { changeSuccessful: false };
  try {
    res = await dispatch(
      changePlan.initiate({
        contractId: productId,
      })
    ).unwrap();
    const { changeSuccessful } = res;
    if (changeSuccessful) {
      await dispatch(refreshUser());
    }
  } catch (e) {
    console.error('changePaymentPlan error', e);
  }
  return res;
});

export const googleLoginThunk = createAsyncThunk<
  void,
  { accessToken?: string },
  { state: CommonRootState }
>("googleLoginThunk", async ({ accessToken }, thunkApi) => {
  const { dispatch } = thunkApi;
  console.log("googleLoginThunk called");
  dispatch(setAuthenticationStatus('userGoogleConnectStarted'));
  try {
    let result: any;
    if (!accessToken) {
      console.log('[login-flow] googleLoginThunk: about to request web auth flow');
      // getAuthToken's interactive Google sign-in can take 30s+ (the user logs in on a new
      // tab). The default 5s message timeout would resolve `undefined` first and drop the
      // token that arrives moments later (issue #88) → tokenless POST → backend 401. Give
      // the round-trip a generous window so the real token is delivered.
      const res = await MessageServiceActions.postMessageToBackgroundWithResponse<{
        accessToken: string;
      }>("launchWebAuthFlow", { timeoutOverride: 120000 });
      console.log('[login-flow] googleLoginThunk: web auth flow returned', { hasAccessToken: !!res?.accessToken });
      accessToken = res?.accessToken;
      if (!accessToken) {
        // No token (browser couldn't reach/complete Google sign-in — e.g. GFW/China, or
        // the user cancelled). Do NOT POST a tokenless {strategy:'google'} — that produces a
        // confusing backend 401 (issue #88). Surface a clear, actionable error instead.
        throw { data: { message: (res as { error?: string })?.error || 'Google sign-in did not return a token. Try email login.' } };
      }
      result = await thunkApi.dispatch(
        authenticate.initiate({
          strategy: "google",
          accessToken,
        })
      ).unwrap();
    } else {
      console.log('[login-flow] googleLoginThunk: about to authenticate with provided access token');
      result = await thunkApi.dispatch(
        authenticate.initiate({
          strategy: "google-token",
          accessToken,
          referralCode: readReferralCode(),
        })
      ).unwrap();
    }
    console.log('[login-flow] googleLoginThunk: authenticate.google returned', { hasResult: !!result, hasUser: !!result?.user, userId: result?.user?._id, hasAccessToken: !!result?.accessToken });
    dispatch(setAuthenticationStatus('userGoogleConnectSuccess'));
    return result as any;
  } catch (e: any) {
    const message = e?.data?.message || 'Google sign-in failed';
    console.log('[login-flow] googleLoginThunk: authenticate.google failed', { status: e?.status, data: e?.data, message });
    dispatch(setAuthError(message));
    dispatch(setAuthenticationStatus('idle'));
    throw e;
  }
});

export const clearStateThunk = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("clearStateThunk", async (_, thunkApi) => {
  const { dispatch } = thunkApi;

  // Account-switch / logout clears all data — reset the live-data debounce so the next login's load
  // is never skipped as "recently loaded" (the prior account's load must not suppress the new one).
  resetLiveDataLoadDebounce();

  const clearStatePromises: unknown[] = [];

  DBReducerMap.forEach((reducer, name) => {
    //console.log(`Clearing state for ${name} reducer`);
    clearStatePromises.push(dispatch(reducer.slice.actions.clearState()));
  });

  await Promise.all(clearStatePromises);
});

export const logoutThunk = createAsyncThunk<void, void, { state: CommonRootState }>(
  "logoutThunk",
  (_, thunkApi) => {
    const { dispatch } = thunkApi;
    try {
      dispatch(logout());
      dispatch(backendApi.util.resetApiState());
      MessageServiceActions.postMessageWithResponse("logout", {
        data: {
          source: "logoutThunk",
        }
      });
      MessageServiceActions.postMessageToBackgroundWithResponse("logout", {
        data: {
          source: "logoutThunk",
        }
      });
      MessageServiceActions.sendMessageToTab("logout", {
        data: {
          source: "logoutThunk",
        }
      });

      localStorage.removeItem(BLUETICKS_TOKEN_KEY);

      dispatch(taskActions.setCurrentBoardView({
        type: 'board',
        id: undefined
      }))

      // Disconnect socket before clearing state
      dispatch(disconnectNotifier());

      dispatch(clearStateThunk());

      // Close UI elements
      dispatch(mainSliceActions.closeCalendarView());
      dispatch(taskActions.setIsTasksPanelOpen(false));
      dispatch(mainSliceActions.toggleSocketModal(false));

      //googleLogout();
    } catch (e) {
      console.error(e);
    }
  }
);

export interface PollRequestOptions {
  user: User;
  timeout?: number;
}

export const refreshUser = createAsyncThunk<void, void, { state: CommonRootState }>(
  "refreshUser",
  async (_, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState();
    const accessToken = state.UserReducer.accessToken ?? undefined;
    await thunkApi
      .dispatch(
        authenticate.initiate({
          strategy: "jwt",
          accessToken,
        })
      )
      .unwrap();
  }
);


export const updateUserPhoneNumberThunk = createAsyncThunk("updateUserPhoneNumberThunk", async (_, thunkApi) => {
  const { dispatch } = thunkApi;
  const state = thunkApi.getState() as CommonRootState;
  if (!state?.UserReducer?.userId) {
    console.log("[updateUserPhoneNumberThunk] No user id found");
    return;
  }

  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: state.UserReducer.userId,
  });

  if (user?.phone) {
    console.log("[updateUserPhoneNumberThunk] Phone number already set");
    return;
  }

  const waMeInfo = await ContentMessageServiceActions.getWaMeInfoTool();
  const phone = waMeInfo && !isWireError(waMeInfo) ? (waMeInfo.phone ?? undefined) : undefined;
  dispatch(DBUserThunks.patch({
    entity: {
      _id: state.UserReducer.userId,
      phone,
    },
  }));
}
);

export const subscribeToFCMTopicThunk = createAsyncThunk("subscribeToFCMTopicThunk", async (fcmToken: string, thunkApi) => {
  const { dispatch } = thunkApi;
  const state = thunkApi.getState() as CommonRootState;
  
  if (!state?.UserReducer?.userId) {
    console.log("[subscribeToFCMTopicThunk] No user id found");
    return { success: false, error: "No user id found" };
  }

  if (!fcmToken) {
    console.log("[subscribeToFCMTopicThunk] No FCM token provided");
    return { success: false, error: "No FCM token provided" };
  }

  console.log("[subscribeToFCMTopicThunk] Subscribing FCM token to user topic for user:", state.UserReducer.userId);
  
  try {
    const response = await dispatch(
      backendApiEndpoints.subscribeToFCMTopic.initiate({
        fcmToken,
      })
    ).unwrap();

    if (response?.success) {
      console.log("[subscribeToFCMTopicThunk] ✅ Successfully subscribed to FCM topic:", response.topicName);
      return { success: true, topicName: response.topicName };
    } else {
      console.error("[subscribeToFCMTopicThunk] ❌ Failed to subscribe to FCM topic:", response?.error);
      return { success: false, error: response?.error || "Unknown error" };
    }
  } catch (error) {
    console.error("[subscribeToFCMTopicThunk] ❌ Error subscribing to FCM topic:", error);
    return { success: false, error: (error instanceof Error ? error.message : String(error)) || "Network error" };
  }
}
);


export const getBlueticksVersion = createAsyncThunk("getBlueticksVersion", async (_, thunkApi) => {
  await BackgorundMessageServiceActions.getCurrentVersion()
});



export const sendEmailToAdminWhenUserDeletesAccount = createAsyncThunk("sendEmailToAdminWhenUserDeletesAccount", async ({userId}: {userId: string}, thunkApi) => {
  const { dispatch } = thunkApi;
  const state = thunkApi.getState() as CommonRootState;
  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: userId,
  });

  if (!user) {
    console.log("[sendEmailToAdminWhenUserDeletesAccount] User not found");
    return;
  }

  const title = `User: ${user.email} has deleted their account`;

  const res = await dispatch(sendEmailFromClient.initiate({
    userDeletedTitle: title,
    userDeletedEmail: user.email ?? '',
  }));

  if ("error" in res) {
    console.log("[sendEmailToAdminWhenUserDeletesAccount] Error sending email", res.error);
  }




});

export const setUserAnalyticsSegmentThunk = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("user/setAnalyticsSegment", async (_, { getState }) => {
  const state = getState();
  const activeSubscription = activeSubscriptionSelector(state);
  const planType = activeSubscription?.planType?.toLowerCase() || 'free';

  // Set user segment for analytics
  setUserSegment({
    type: 'authenticated_user',
    source: isExtensionContext() ? 'extension' : 
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 'mobile' : 'web_app',
    plan_type: planType as 'free' | 'basic' | 'standard' | 'pro'
  });
});

export const toggleLabelsFeature = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("toggleLabelsFeature", async (_, { getState, dispatch }) => {
  const state = getState();
  const [user] = userSelectByQuery(state.DBUserReducer, {
    _id: state.UserReducer.userId,
  });

  if (!user) return;

  const isLabelsDisabled = !user.isLabelsDisabled;

  dispatch(
    DBUserThunks.patch({
      entity: {
        _id: user._id,
        isLabelsDisabled,
      },
    })
  );

  // Update local state immediately for UI reactivity
  dispatch(setUser({ ...user, isLabelsDisabled }));

  // When disabling labels, reset to show all chats
  if (isLabelsDisabled) {
    dispatch(labelActions.setActiveLabel(''));
    ContentMessageServiceActions.waUpdateShouldAppearChatsByListTool(null);
  }
});
